import React, { useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import styles from './ReadingViewAll.module.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Loading from '../../../common/HousGlass Loader/Loading';
import { useEbooksByGenreSlug,useCategoryMetaBySlug } from '../../../hooks/useEbooksHooks';
import Footer from '../../../common/UI/Footer';
import { Helmet } from 'react-helmet';

function ReadingViewAll() {
  const { param } = useParams(); // This is just the genre name from URL
  const navigate = useNavigate();
  const location = useLocation();
  const { data: categoryMeta } = useCategoryMetaBySlug("Ebook",param);

  
  // Parse page from URL query params or default to 1
  const urlSearchParams = new URLSearchParams(location.search);
  const pageFromUrl = parseInt(urlSearchParams.get('page') || '1', 10);
  
  // Local pagination state (will be synced with URL)
  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  // Changed books per page to 50
  const BOOKS_PER_PAGE = 50;

  // Use the hook with the genre name from URL params and pagination options
  const { 
    ebooks,
    isLoading,
    error,
    pagination,
    refetch
  } = useEbooksByGenreSlug(param, {
    enabled: !!param,
    refetchOnWindowFocus: false
  });
  
  // Fetch data with the current page whenever it changes
  useEffect(() => {
    // Update URL with current page using navigate
    const searchParams = new URLSearchParams();
    if (currentPage > 1) {
      searchParams.set('page', currentPage.toString());
    }
    
    // Update the URL using React Router's navigate
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
    
    // Directly call the API with currentPage and BOOKS_PER_PAGE
    const fetchData = async () => {
      try {
        await refetch({
          queryParams: {
            page: currentPage,
            limit: BOOKS_PER_PAGE
          }
        });
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    
    fetchData();
  }, [currentPage, navigate, location.pathname, param, refetch]);
  
  // Extract the actual books data from the response
  const filteredBooks = useMemo(() => {
    if (!ebooks) return [];
    return ebooks || [];
  }, [ebooks]);

  // Get top rated books for the showcase
  const topBooks = useMemo(() => {
    if (!filteredBooks.length) return [];
    
    return [...filteredBooks]
      .sort((a, b) => b.likes_count - a.likes_count)
      .slice(0, 5);
  }, [filteredBooks]);
  
  // Function to determine which cover image to use
  const getCoverImage = (book) => {
    // If book has series data with a cover image, use that
    if (book.series && book.series.cover_image_url) {
      return book.series.cover_image_url;
    }
    // Otherwise use the book's own cover image
    return book.cover_image_url_for_computer;
  };
  
  // Handle navigation to book details
  const handleBookClick = (book) => {
    // If there's a series_id, navigate to series details, otherwise navigate to the book's detail
    if(book.series_id !== null) {
      const navigateSlug = book.slug;
      navigate(`/book-details/${navigateSlug}`);
    } else {
      navigate(`/book-detail/${book.slug}`);
    }
  };

  // Pagination handlers with direct navigation
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      
      // Navigate directly to the previous page
      const searchParams = new URLSearchParams();
      if (prevPage > 1) {
        searchParams.set('page', prevPage.toString());
      }
      
      navigate({
        pathname: location.pathname,
        search: searchParams.toString()
      });
      
      window.scrollTo(0, 0); // Scroll to top when changing pages
    }
  };

  const handleNextPage = () => {
    if (pagination && currentPage < pagination.totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      
      // Navigate directly to the next page
      const searchParams = new URLSearchParams();
      searchParams.set('page', nextPage.toString());
      
      navigate({
        pathname: location.pathname,
        search: searchParams.toString()
      });
      
      window.scrollTo(0, 0); // Scroll to top when changing pages
    }
  };

  // Loading states
  if (isLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  // Error states
  if (error) {
    return <div className={styles.error}>Error loading content: {error.message || 'Unknown error'}</div>;
  }

  // No books found
  if (!filteredBooks || filteredBooks.length === 0) {
    return <div className={styles.noBooks}>No books found for genre: {param}</div>;
  }
  
  // Only show pagination controls if there are more than BOOKS_PER_PAGE books in total
  const showPagination = pagination && pagination.totalItems > BOOKS_PER_PAGE;
  
  return (
    <>
    <Helmet>
      <title>{categoryMeta?.page_title || `All ${param} Books - SunoKahaniyan`}</title>
      <meta
        name="description"
        content={
          categoryMeta?.meta_description ||
          `Explore all ${param} books available on SunoKahaniyan.`
        }
      />
      <meta
        name="keywords"
        content={
          categoryMeta?.meta_keywords ||
          `${param}, suno kahaniyan, audio books, ebooks`
        }
      />
    </Helmet>
    <div className={styles.container}>
      {/* Showcase Section */}
      <section className={styles.showcase}>
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={20}
          navigation
          pagination={{ clickable: true }}
          breakpoints={{
            320: { slidesPerView: 1 },
            640: { slidesPerView: 1 },
            968: { slidesPerView: 1 },
            1200: { slidesPerView: 1 }
          }}
          className={styles.showcaseSwiper}
        >
          {topBooks.map((book) => (
            <SwiperSlide key={book.id} onClick={() => handleBookClick(book)}>
              <div className={styles.showcaseSlide}>
                <div
                  className={styles.backgroundImage}
                  style={{ backgroundImage: `url(${getCoverImage(book)})` }}
                />
                <div className={styles.contentWrapper}>
                  <div className={styles.bookDetails}>
                    <h1 className={styles.title}>{book.title}</h1>
                    <p className={styles.author}>By {book.author}</p>
                    <p className={styles.category}>{book.genre_name}</p>
                  </div>
                  <div className={styles.coverImageContainer}>
                    <img
                      src={getCoverImage(book)}
                      alt={book.title}
                      className={styles.coverImage}
                    />
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Book Grid */}
      <section className={styles.booksGrid}>
        <h2 className={styles.gridTitle}>All {param} Books</h2>
        <div className={styles.grid}>
          {filteredBooks.map((book) => (
            <div 
              key={book.id} 
              className={styles.bookCard} 
              onClick={() => handleBookClick(book)}
            >
              <div className={styles.imageContainer}>
                <img src={getCoverImage(book)} alt={book.title} />
              </div>
            </div>
          ))}
        </div>
        
        {/* Pagination Controls - Only shown when there are more than BOOKS_PER_PAGE total books */}
        {showPagination && (
          <div className={styles.paginationControls}>
            <button 
              className={`${styles.paginationButton} ${currentPage === 1 ? styles.disabled : ''}`}
              onClick={handlePreviousPage}
              disabled={currentPage === 1 || isLoading}
            >
              Previous
            </button>
            
            <span className={styles.pageInfo}>
              Page {currentPage} of {pagination.totalPages}
            </span>
            
            <button 
              className={`${styles.paginationButton} ${currentPage >= pagination.totalPages ? styles.disabled : ''}`}
              onClick={handleNextPage}
              disabled={currentPage >= pagination.totalPages || isLoading}
            >
              Next
            </button>
          </div>
        )}
        
        {/* Debugging info - remove in production */}
        <div style={{ color: 'white', fontSize: '12px', marginTop: '10px', textAlign: 'center' }}>
          Showing {filteredBooks.length} books on page {currentPage}
        </div>
      </section>
    </div>
    <Footer/>
    </>
  );
}

export default ReadingViewAll;